import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import Form from "../../../../common/forms/Form";
import FormInput from "../../../../common/forms/FormInput";
import ErrorBanner from "../../../../common/forms/ErrorBanner";
import BackBar from "../../../../common/nav/BackBar";
import SaveButton from "../../../../common/nav/SaveButton";
import NotificationModal from "../../../../common/settings-modal/NotificationModal";

import SelenaA from "../../../../assets/img/service/selena-a.webp";

import { ValType } from "../../../../utils/ValidationFunctions";
import { RequestState } from "../../../../utils/RequestState";

import styles from "../EditBase.module.scss";
import LoadingSpinner from "../../../../common/LoadingSpinner";

type Props = {
  handleClose: () => void;
  display_name?: string;
  handleName: (value: string) => void;
  curPass: string;
  newPass: string;
  confirmPass: string;
  handleCurPass: (value: string) => void;
  handleNewPass: (value: string) => void;
  handleConfirmPass: (value: string) => void;
  handleUserInfoSubmit: () => Promise<void>;
  errorMessage: string;
  requestState: RequestState;
};

const EditAccountInfo: React.FC<Props> = ({
  handleClose,
  display_name,
  handleName,
  curPass,
  newPass,
  confirmPass,
  handleCurPass,
  handleNewPass,
  handleConfirmPass,
  handleUserInfoSubmit,
  errorMessage,
  requestState,
}) => {
  const { t } = useTranslation();

  const genNotificationColor = () => {
    switch (requestState) {
      case RequestState.Pending:
      case RequestState.Failed:
        return "#fe493d";
      case RequestState.Doing:
        return "#f79b00";
      case RequestState.Success:
        return "#27c900";
      default:
        return "";
    }
  };

  const genNotificationMessage = () => {
    switch (requestState) {
      case RequestState.Pending:
        return "保存していない変更があります";
      case RequestState.Doing:
        return "通信中です";
      case RequestState.Success:
        return "保存が完了しました";
      case RequestState.Failed:
        return "保存に失敗しました";
      default:
        return "";
    }
  };

  const genNotificationIcon = (): IconProp | undefined => {
    switch (requestState) {
      case RequestState.Pending:
      case RequestState.Failed:
        return ["fal", "exclamation-circle"];
      case RequestState.Success:
        return ["fal", "check-circle"];
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (requestState === RequestState.Doing) {
      window.scrollTo(0, 0);
    }
  }, [requestState]);

  return (
    <div>
      {requestState !== RequestState.Doing ? (
        <>
          <BackBar
            title={t("アカウント編集")}
            editable={true}
            handleClose={handleClose}
            saveHandler={handleUserInfoSubmit}
            hideSaveButton={
              requestState !== RequestState.Pending ? true : false
            }
          />
          <div className={`${styles["p-edit__inner"]} ${styles["x-padding"]}`}>
            <Form handler={() => {}}>
              <div className={styles["p-edit__title"]}>
                {t("会員名")}
                <div className={styles["p-edit__title-desc"]}>
                  {`SELeCTで使用する 会員名 を入力してください、ニックネームや会社名でも大丈夫です。\n会員名はSELeCT上に表示される場合がございます。`}
                </div>
              </div>
              <div className={styles["p-edit__form"]}>
                <FormInput
                  type="text"
                  label="会員名"
                  label_class={styles["p-edit__form-title"]}
                  name="sonic_name"
                  val_type={ValType.NotNull}
                  style_class={styles["p-edit__form-input"]}
                  id="sonic_name"
                  handler={handleName}
                  initial_value={display_name}
                />
              </div>
              <div className={styles["p-edit__title"]}>{t("パスワード")}</div>
              <div className={styles["p-edit__form__pass-error"]}>
                <ErrorBanner
                  warn={errorMessage ? true : false}
                  message={errorMessage}
                />
              </div>

              <div className={`${styles["p-edit__form"]} ${styles["pass"]}`}>
                <FormInput
                  type="password"
                  label="現在のパスワード"
                  label_class={styles["p-edit__form-title"]}
                  name="current_pass"
                  placeholder="現在のパスワード"
                  val_type={ValType.NotNull}
                  style_class={styles["p-edit__form-input"]}
                  id="current_pass"
                  handler={handleCurPass}
                  initial_value={curPass}
                />
              </div>
              <div className={`${styles["p-edit__form"]} ${styles["pass"]}`}>
                <FormInput
                  type="password"
                  label="新しいパスワード"
                  label_class={styles["p-edit__form-title"]}
                  placeholder="新しいパスワード"
                  name="new_pass"
                  val_type={ValType.PasswordFormat}
                  style_class={styles["p-edit__form-input"]}
                  id="new_pass"
                  handler={handleNewPass}
                  initial_value={newPass}
                />
              </div>
              <div className={`${styles["p-edit__form"]} ${styles["pass"]}`}>
                <FormInput
                  type="password"
                  label="新しいパスワード(確認)"
                  label_class={styles["p-edit__form-title"]}
                  name="new_pass_confirm"
                  val_type={ValType.PasswordFormat}
                  style_class={styles["p-edit__form-input"]}
                  id="new_pass_confirm"
                  handler={handleConfirmPass}
                  initial_value={confirmPass}
                />
              </div>
            </Form>
          </div>
          {requestState === RequestState.Pending ? (
            <SaveButton onClick={handleUserInfoSubmit} />
          ) : null}
        </>
      ) : (
        <LoadingSpinner space={1} />
      )}
      {requestState !== RequestState.Waiting ? (
        <NotificationModal
          color={genNotificationColor()}
          icon_class={genNotificationIcon()}
          message={genNotificationMessage()}
          img_src={requestState === RequestState.Success ? SelenaA : undefined}
        />
      ) : null}
    </div>
  );
};

export default EditAccountInfo;
